<template>
  <Dialog :visible="manageSearchCredentialDialogVisible"
          @update:visible="handleClose">
    <template #header>
      {{ manageSearchCredentialId ? 'Edit' : 'Add' }} Search Credential
    </template>

    <div class="form-element">
      Name:
      <InputText v-model="manageSearchCredentialName" :invalid="manageSearchCredentialNameInvalid" class="form-element full-width" type="text"
                 @change="manageSearchCredentialNameChange"/>
      <div v-if="manageSearchCredentialNameInvalid">
        <InlineMessage severity="error">Credential Name is required.</InlineMessage>
      </div>
      <small class="text-gray-500">A descriptive name for this search credential</small>
    </div>

    <div class="form-element">
      Search Engine:
      <Dropdown v-model="manageSearchCredentialEngine" :options="searchEngineOptions" optionLabel="name" optionValue="value" class="form-element full-width" />
      <small class="text-gray-500">The search engine provider to use</small>
    </div>

    <Fieldset class="mt-2" @update:collapsed="updateCredentialsCollapsed" :legend="(manageSearchCredentialId? 'Input New ' : '') + 'Credential Value'"
              :toggleable="!!manageSearchCredentialId" :collapsed="!!manageSearchCredentialId">
      <div class="form-element">
        API Key:
        <InputText v-model="manageSearchCredentialApiKey" :invalid="manageSearchCredentialApiKeyInvalid" class="form-element full-width" type="text"
                   @change="manageSearchCredentialApiKeyChange"/>
        <div v-if="manageSearchCredentialApiKeyInvalid">
          <InlineMessage severity="error">API Key is required.</InlineMessage>
        </div>
        <small class="text-gray-500">
          <template v-if="manageSearchCredentialEngine === 'google'">Your Google Custom Search API key</template>
          <template v-else-if="manageSearchCredentialEngine === 'tavily'">Your Tavily API key</template>
        </small>
      </div>

      <div class="form-element mt-2" v-if="manageSearchCredentialEngine === 'google'">
        Search Engine ID:
        <InputText v-model="manageSearchCredentialEngineId" :invalid="manageSearchCredentialEngineIdInvalid" class="form-element full-width" type="text"
                   @change="manageSearchCredentialEngineIdChange"/>
        <div v-if="manageSearchCredentialEngineIdInvalid">
          <InlineMessage severity="error">Search Engine ID is required.</InlineMessage>
        </div>
        <small class="text-gray-500">Your Google Programmable Search Engine ID</small>
      </div>
    </Fieldset>

    <template #footer>
      <div class="align-right">
        <Button class="mr" icon="pi pi-times" label="Cancel" severity="secondary" type="button" @click="handleClose"></Button>
        <Button :disabled="manageSearchCredentialSubmitting" :loading="manageSearchCredentialSubmitting" label="Save" type="button" @click="saveSearchCredential"></Button>
      </div>
    </template>
  </Dialog>

  <div v-if="hasAdminAccess">
    <ProgressSpinnerContainer v-if="modelDeploymentsLoading" />
    <div v-else class="card">
      <div class="header-container mb flex">
        <h2 class="flex-1">Search Credentials</h2>
        <div class="align-right mr">
          <Button @click="manageSearchCredential(null)"><i class="pi pi-plus"></i> Add Search Credential</Button>
        </div>
      </div>
      <ScrollPanel class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="searchCredentials">
          <Column field="name" header="Name"></Column>
          <Column field="metadata.engine" header="Search Engine">
            <template #body="slotProps">
              {{ searchEngineOptions.find(opt => opt.value === slotProps.data.metadata?.engine)?.name || slotProps.data.metadata?.engine }}
            </template>
          </Column>
          <Column header="Actions">
            <template #body="slotProps">
              <Button v-if="slotProps.data.platformAccountId > 0" severity="info" @click="manageSearchCredential(slotProps.data)" label="Edit"></Button>
              <Button v-if="slotProps.data.platformAccountId > 0" class="ml" severity="danger" @click="deleteSearchCredential(slotProps.data)" label="Delete"></Button>
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </div>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { useConfirm } from "primevue/useconfirm";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";
import { useUsers } from "../composables/entities.js";
import { selectedAccount } from "../store/state.js";
import { deleteRequest, patch, post } from "../util/api.js";
import { decodeModelDeploymentCredential } from "../util/util.js";
import { useModelDeployments } from "../composables/entities.js";

const { modelDeploymentCredentials, modelDeploymentsLoading, searchCredentials } = useModelDeployments()

const { hasAdminAccess } = useUsers()

const toast = useToast()

const confirm = useConfirm()

const searchEngineOptions = [
  { name: 'Google', value: 'google' },
  { name: 'Tavily', value: 'tavily' }
]

async function deleteSearchCredential(data) {
  confirm.require({
    message: 'Are you sure you wish to delete ' + data.name + '?',
    header: 'Confirm Delete Search Credential',
    icon: 'pi pi-info-circle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        modelDeploymentsLoading.value = true
        const request = await deleteRequest('/crbn-tech/ai-model-deployment-credentials/' + data.id)
        if (request.id) {
          searchCredentials.value = searchCredentials.value.filter(md => String(md.id) !== String(request.id))
        }
        else {
          handleError()
        }
      }
      catch (e) {
        handleError(e)
      }
      finally {
        modelDeploymentsLoading.value = false
      }
      return true
    },
    reject: () => {
      return true
    }
  })
}

watch(modelDeploymentsLoading, (newValue)=> {
  if (!newValue) {
    if (!hasAdminAccess.value) {
      //router.replace('/')
    }
  }
}, { immediate: true })

const manageSearchCredentialDialogVisible = ref(false)
const manageSearchCredentialSubmitting = ref(false)

const manageSearchCredentialId = ref(null)
const manageSearchCredentialName = ref(null)
const manageSearchCredentialApiKey = ref(null)
const manageSearchCredentialEngineId = ref(null)
const manageSearchCredentialEngine = ref('google')

const manageSearchCredentialNameInvalid = ref(false)
const manageSearchCredentialApiKeyInvalid = ref(false)
const manageSearchCredentialEngineIdInvalid = ref(false)

function manageSearchCredentialNameChange() {
  manageSearchCredentialNameInvalid.value = !manageSearchCredentialName.value
}

function manageSearchCredentialApiKeyChange() {
  manageSearchCredentialApiKeyInvalid.value = !manageSearchCredentialApiKey.value
}

function manageSearchCredentialEngineIdChange() {
  manageSearchCredentialEngineIdInvalid.value = !manageSearchCredentialEngineId.value
}

function handleClose() {
  manageSearchCredentialId.value = null
  manageSearchCredentialName.value = null
  manageSearchCredentialApiKey.value = null
  manageSearchCredentialEngineId.value = null
  manageSearchCredentialEngine.value = 'google'

  manageSearchCredentialNameInvalid.value = false
  manageSearchCredentialApiKeyInvalid.value = false
  manageSearchCredentialEngineIdInvalid.value = false

  manageSearchCredentialDialogVisible.value = false
  manageSearchCredentialSubmitting.value = false
}

function manageSearchCredential(data) {
  manageSearchCredentialDialogVisible.value = true
  manageSearchCredentialId.value = data?.id
  manageSearchCredentialName.value = data?.name
  manageSearchCredentialEngine.value = data?.metadata?.engine || 'google'
  manageSearchCredentialApiKey.value = data?.metadata?.apiKey
  manageSearchCredentialEngineId.value = data?.metadata?.engineId
}

function handleError(e) {
  manageSearchCredentialSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'An error has occurred.  Please try again.'
  })
}

function handleCredentialError(e) {
  manageSearchCredentialSubmitting.value = false
  toast.add({
    severity: 'error',
    group: 'mainToast',
    life: 5000,
    detail: 'The credential metadata was updated, but the credential value could not be saved.  Please try again.'
  })
}

function resetValidations() {
  manageSearchCredentialNameInvalid.value = false
  manageSearchCredentialApiKeyInvalid.value = false
  manageSearchCredentialEngineIdInvalid.value = false
}

async function saveSearchCredential() {
  let valid = true
  resetValidations()
  if (!manageSearchCredentialName.value) {
    manageSearchCredentialNameInvalid.value = true
    valid = false
  }
  if (!manageSearchCredentialApiKey.value) {
    manageSearchCredentialApiKeyInvalid.value = true
    valid = false
  }
  if (manageSearchCredentialEngine.value === 'google' && !manageSearchCredentialEngineId.value) {
    manageSearchCredentialEngineIdInvalid.value = true
    valid = false
  }

  if (!valid) {
    return false
  }

  const metadata = {
    keyType: 'search',
    engine: manageSearchCredentialEngine.value
  }

  if (manageSearchCredentialId.value) {
    const payload = {
      title: manageSearchCredentialName.value,
      metadata: JSON.stringify(metadata)
    }
    manageSearchCredentialSubmitting.value = true
    try {
      let result = await patch('/crbn-tech/ai-model-deployment-credentials/' + manageSearchCredentialId.value, payload)
      if (result.id) {
        try {
          if (manageSearchCredentialApiKey.value && !credentialsCollapsed.value) {
            const credentialPayload = {
              credential_value: JSON.stringify({
                engine: manageSearchCredentialEngine.value,
                ...(manageSearchCredentialEngine.value === 'google' ? { engineId: manageSearchCredentialEngineId.value } : {}),
                apiKey: manageSearchCredentialApiKey.value
              })
            }
            const credentialResult = await post('/crbn-tech/ai-model-deployment-credentials/' + result.id + '/credentials', credentialPayload)
            if (credentialResult.id) {
              result = credentialResult
            }
            else {
              handleError();
            }
          }
        }
        catch (e) {
          handleError(e)
        }
        for (let i = 0; i < modelDeploymentCredentials.value.length; i++) {
          if (String(modelDeploymentCredentials.value[i].id) === String(result.id)) {
            modelDeploymentCredentials.value[i] = decodeModelDeploymentCredential(result)
          }
        }
      }
      else {
        handleError();
      }
      handleClose()
    }
    catch (e) {
      manageSearchCredentialSubmitting.value = false
      handleError(e)
    }
  }
  else {
    const payload = {
      title: manageSearchCredentialName.value,
      cephisso_platform_accounts_id: selectedAccount.value,
      metadata: JSON.stringify(metadata)
    }
    manageSearchCredentialSubmitting.value = true
    try {
      let result = await post('/crbn-tech/ai-model-deployment-credentials', payload)
      if (result.id) {
        try {
          if (manageSearchCredentialApiKey.value) {
            const credentialPayload = {
              credential_value: JSON.stringify({
                engine: manageSearchCredentialEngine.value,
                ...(manageSearchCredentialEngine.value === 'google' ? { engineId: manageSearchCredentialEngineId.value } : {}),
                apiKey: manageSearchCredentialApiKey.value
              })
            }
            const credentialResult = await post('/crbn-tech/ai-model-deployment-credentials/' + result.id + '/credentials', credentialPayload)
            if (credentialResult.id) {
              result = credentialResult
            }
            else {
              handleCredentialError();
            }
          }
        }
        catch (e) {
          handleError(e)
        }

        modelDeploymentCredentials.value.push(decodeModelDeploymentCredential(result))
      }
      else {
        handleError()
      }
      handleClose()
    }
    catch (e) {
      manageSearchCredentialSubmitting.value = false
      handleError(e)
    }
  }
}

const credentialsCollapsed = ref(false)
function updateCredentialsCollapsed(v) {
  credentialsCollapsed.value = v
}

</script>

<style scoped>
</style> 